import Head from 'next/head';
import {useMemo} from 'react';

import {HeadSEO} from '@udemy/head-seo';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {SubscriptionPlanProductType, SubscriptionPlansProvider} from '@udemy/subscription-graphql';

import {getLayout} from 'src/components/layout';
import {SubscriptionCTAExperimentProvider} from 'src/contexts/subscription-cta-experiment-context';
import {useCanonicalUrl} from 'src/hooks/useCanonicalUrl';
import {useCouponCodeParam} from 'src/hooks/useCouponCodeParam';
import {META_DEFAULT_BASE_URL} from 'src/lib/constants';
import {PERSONAL_PLAN_REVAMP} from 'src/lib/experimentation-constants';

import {usePersonalPlanMarketingUrl} from './hooks/usePersonalPlanMarketingUrl';
import {PersonalPlanLandingPageBody} from './personal-plan-landing-page-body.react-component';
import {
    PersonalPlanCampaignNoticeProvider,
    PersonalPlanSubscriptionCheckoutEntryProvider,
} from './personal-plan-subscription-checkout-entry-context';

export function PersonalPlanLandingPageFeature() {
    const {url: canonicalUrl} = useCanonicalUrl([], META_DEFAULT_BASE_URL);
    const couponCode = useCouponCodeParam();
    const marketingURL = usePersonalPlanMarketingUrl(canonicalUrl);

    const subscriptionPlanQueryVariables = useMemo(
        () => [
            {
                filters: {
                    productType: SubscriptionPlanProductType.Consumersubscription,
                },
                marketingURL,
                ...(couponCode ? {couponCode} : {}),
            },
            {
                filters: {
                    productType: SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
                },
                marketingURL,
                ...(couponCode ? {couponCode} : {}),
            },
            {
                filters: {
                    productType: SubscriptionPlanProductType.GrowWithGoogle,
                },
                marketingURL,
                ...(couponCode ? {couponCode} : {}),
            },
        ],
        [marketingURL, couponCode],
    );

    const {featureVariantAssignments} = useFeatures([PERSONAL_PLAN_REVAMP]);

    const isPersonalPlanRevampEnabled =
        featureVariantAssignments.get(PERSONAL_PLAN_REVAMP)?.configuration ?? {};

    return (
        <>
            <HeadSEO
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            <SubscriptionCTAExperimentProvider
                context={{
                    showCancelAnytime: true,
                }}
            >
                <SubscriptionPlansProvider
                    subscriptionPlanQueryVariables={subscriptionPlanQueryVariables}
                >
                    <PersonalPlanSubscriptionCheckoutEntryProvider>
                        <PersonalPlanCampaignNoticeProvider>
                            <PersonalPlanLandingPageBody
                                isPPLPRevamp={isPersonalPlanRevampEnabled?.isPPLPRevamp}
                            />
                        </PersonalPlanCampaignNoticeProvider>
                    </PersonalPlanSubscriptionCheckoutEntryProvider>
                </SubscriptionPlansProvider>
            </SubscriptionCTAExperimentProvider>
        </>
    );
}

PersonalPlanLandingPageFeature.getLayout = getLayout;
